import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
  Col,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import { typeOptions } from '../../../containers/ipma/Cis/Certificates/CertificatesOptions';
import { holderAuthorizeOptions, visibilityOptions } from '../CisOptions';
import { exportCertificatesToExcel } from '../../../api/services/cis';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import checkPermissions from '../../../helpers/checkPermissions';
import { useMemo } from 'react';
import { formatDate } from 'helpers/date';

const Certificates = ({
  loading,
  type,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeCertificate,
  countries,
  certificationBodies,
  levelDomainsLevels,
  levelDomainsDomains,
  languages,
  loadCertificatesAttributes,
  organizations,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const gcOrgs = useMemo(() => organizations.filter((org) => org.type?.toLowerCase() === 'gc'), [
    organizations,
  ]);

  const isIpmaCvmb = type === 'ipma-cvmb';
  const manageCisPermission =
    (type === 'ipma' && checkPermissions({ roles: ['ipma_cis'] }, userPermissions || [])) ||
    (type === 'ma' && checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions || []));

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ''),
      );
      await exportCertificatesToExcel(filteredFilters);
    } catch (error) {
      console.error('Error exporting certificates:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleNavigateExportReport = () => {
    if (isIpmaCvmb) {
      history.push(`/ipma/cvmb-cis/certificates/export-report`);
    } else {
      history.push(`/${type}/cis/certificates/export-report`);
    }
  };

  useEffect(() => {
    if (isIpmaCvmb) {
      if (document.getElementById('ipma-cis-cvmb-statistics-actions-portal')) {
        setPortalReady(true);
      }
    } else {
      if (document.getElementById(`${type}-cis-buttons-portal`)) {
        setPortalReady(true);
      }
    }
  }, [isIpmaCvmb, type]);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadCertificatesAttributes();
  }, []);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.certificateHolder || ''}
                  onFilter={(val) => handleColumnFilterChange('certificateHolder', val)}
                  filterPlaceholder={t('page.user.cis.certificates.certificateHolderPlaceholder')}
                  label={t('page.user.cis.certificates.certificateHolder')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.level || ''}
                  onFilter={(val) => handleColumnFilterChange('level', val)}
                  filterOptions={levelDomainsLevels}
                  filterPlaceholder={t('page.user.cis.agreements.levelPlaceholder')}
                  label={t('page.user.cis.agreements.level')}
                  sort
                  sortDirection={getSortDirection('level')}
                  onSort={(dir) => handleColumnSort('level', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.domain || ''}
                  onFilter={(val) => handleColumnFilterChange('domain', val)}
                  filterOptions={levelDomainsDomains}
                  filterPlaceholder={t('page.user.cis.agreements.domainPlaceholder')}
                  label={t('page.user.cis.agreements.domain')}
                  sort
                  sortDirection={getSortDirection('domain')}
                  onSort={(dir) => handleColumnSort('domain', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range-year-select"
                  filterValueFrom={filters.issueDateFrom || ''}
                  filterValueTo={filters.issueDateTo || ''}
                  filterValueYear={filters.issueDateYear || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('issueDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('issueDateTo', val)}
                  onFilterYear={(val) => handleColumnFilterChange('issueDateYear', val)}
                  filterPlaceholder={t('page.user.cis.certificates.issueDatePlaceholder')}
                  label={t('page.user.cis.certificates.issueDate')}
                  sort
                  sortDirection={getSortDirection('issueDate')}
                  onSort={(dir) => handleColumnSort('issueDate', dir)}
                />
              </CisTable.StickyHeading>
              {type === 'ipma' && (
                <CisTable.StickyHeading>
                  <TableHeaderCell
                    filter
                    filterType="select"
                    filterValue={filters.certificationBody || ''}
                    onFilter={(val) => handleColumnFilterChange('certificationBody', val)}
                    filterOptions={certificationBodies}
                    filterPlaceholder={t('page.user.cis.certificates.certificationBodyPlaceholder')}
                    label={t('page.user.cis.certificates.certificationBody')}
                  />
                </CisTable.StickyHeading>
              )}
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.clientCountry || ''}
                  onFilter={(val) => handleColumnFilterChange('clientCountry', val)}
                  filterOptions={countries}
                  filterPlaceholder={t('page.user.cis.certificates.clientCountryPlaceholder')}
                  label={t('page.user.cis.certificates.clientCountry')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.type || ''}
                  onFilter={(val) => handleColumnFilterChange('type', val)}
                  filterOptions={typeOptions}
                  filterPlaceholder={t('page.user.cis.certificates.typePlaceholder')}
                  label={t('page.user.cis.certificates.type')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.validUntilFrom || ''}
                  filterValueTo={filters.validUntilTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('validUntilFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('validUntilTo', val)}
                  filterPlaceholder={t('page.user.cis.certificates.validUntilPlaceholder')}
                  label={t('page.user.cis.certificates.validUntil')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.certificateNumber || ''}
                  onFilter={(val) => handleColumnFilterChange('certificateNumber', val)}
                  filterPlaceholder={t('page.user.cis.certificates.certificateNumberPlaceholder')}
                  label={t('page.user.cis.certificates.certificateNumber')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.holderAuthorized || ''}
                  onFilter={(val) => handleColumnFilterChange('holderAuthorized', val)}
                  filterOptions={holderAuthorizeOptions}
                  filterPlaceholder={t('page.user.cis.certificates.holderAuthorizedPlaceholder')}
                  label={t('page.user.cis.certificates.holderAuthorized')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.visibility || ''}
                  onFilter={(val) => handleColumnFilterChange('visibility', val)}
                  filterOptions={visibilityOptions}
                  filterPlaceholder={t('page.user.cis.certificates.visibilityPlaceholder')}
                  label={t('page.user.cis.certificates.visibility')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.invoiceNumber || ''}
                  onFilter={(val) => handleColumnFilterChange('invoiceNumber', val)}
                  filterPlaceholder={t('page.user.cis.certificates.invoiceNumberPlaceholder')}
                  label={t('page.user.cis.certificates.invoiceNumber')}
                  sort
                  sortDirection={getSortDirection('invoiceNumber')}
                  onSort={(dir) => handleColumnSort('invoiceNumber', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.invoiceDateFrom || ''}
                  filterValueTo={filters.invoiceDateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('invoiceDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('invoiceDateTo', val)}
                  filterPlaceholder={t('page.user.cis.certificates.invoiceDatePlaceholder')}
                  label={t('page.user.cis.certificates.invoiceDate')}
                  sort
                  sortDirection={getSortDirection('invoiceDate')}
                  onSort={(dir) => handleColumnSort('invoiceDate', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.organization || ''}
                  onFilter={(val) => handleColumnFilterChange('organization', val)}
                  filterOptions={gcOrgs}
                  filterPlaceholder={t('page.user.cis.certificates.organizationPlaceholder')}
                  label={t('page.user.cis.certificates.organization')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.language || ''}
                  onFilter={(val) => handleColumnFilterChange('language', val)}
                  filterOptions={languages}
                  filterPlaceholder={t('page.user.cis.certificates.languagePlaceholder')}
                  label={t('page.user.cis.certificates.language')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.headSignature || ''}
                  onFilter={(val) => handleColumnFilterChange('headSignature', val)}
                  filterPlaceholder={t('page.user.cis.certificates.headSignaturePlaceholder')}
                  label={t('page.user.cis.certificates.headSignature')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.assessorSignature || ''}
                  onFilter={(val) => handleColumnFilterChange('assessorSignature', val)}
                  filterPlaceholder={t('page.user.cis.certificates.assessorSignaturePlaceholder')}
                  label={t('page.user.cis.certificates.assessorSignature')}
                />
              </CisTable.StickyHeading>
              {!isIpmaCvmb && manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.certificates.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={isIpmaCvmb ? 14 : 15}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={isIpmaCvmb ? 14 : 15}>
                  {t('global.noResultsFound')}
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificates.certificateHolder')}:
                      </Table.Label>
                      {[item.certificateHolder?.firstName, item.certificateHolder?.middleName, item.certificateHolder?.lastName].filter(Boolean).join(' ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.level')}:</Table.Label>
                      {item.levelDomain?.level?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.domain')}:</Table.Label>
                      {item.levelDomain?.domain?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.issueDate')}:</Table.Label>
                      {item.issueDate && formatDate(item.issueDate)}
                    </CisTable.Cell>
                    {type === 'ipma' && (
                      <CisTable.Cell>
                        <Table.Label>
                          {t('page.user.cis.certificates.certificationBody')}:
                        </Table.Label>
                        {item.certificationBody?.name}
                      </CisTable.Cell>
                    )}
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.clientCountry')}:</Table.Label>
                      {item.memberAssociation?.country?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.type')}:</Table.Label>
                      {item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : ''}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.validUntil')}:</Table.Label>
                      {item.validUntil && formatDate(item.validUntil)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificates.certificateNumber')}:
                      </Table.Label>
                      {item.certId}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.holderAuthorized')}:</Table.Label>
                      {item.holderAuthorized.toString()}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.visibility')}:</Table.Label>
                      {item.visibility.toString()}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.invoiceNumber')}:</Table.Label>
                      {item.isHistorical ? '99999' : item.invoice?.referenceNumber}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.invoiceDate')}:</Table.Label>
                      {item.invoice?.invoiceDate &&
                        formatDate(item.invoice?.invoiceDate)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.organization')}:</Table.Label>
                      {item.organization?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.language')}:</Table.Label>
                      {item.language?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.headSignature')}:</Table.Label>
                      {(() => {
                        const head = item.signatures.find(
                          (signature) => signature.assessorType === 'H',
                        );
                        if (!head) return '';
                        const user = head.user || {};
                        const firstName = user.firstName || head.userFirstName;
                        const lastName = user.lastName || head.userLastName;
                        return [firstName, lastName].filter(Boolean).join(' ');
                      })()}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificates.signedAssessor')}:</Table.Label>
                      {(() => {
                        const signed = item.signatures.find(
                          (signature) => signature.assessorType === 'S',
                        );
                        if (!signed) return '';
                        const user = signed.user || {};
                        const firstName = user.firstName || signed.userFirstName;
                        const lastName = user.lastName || signed.userLastName;
                        return [firstName, lastName].filter(Boolean).join(' ');
                      })()}
                    </CisTable.Cell>
                    {!isIpmaCvmb && manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.certificates.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/${type}/cis/certificates/edit`);
                            }}
                          />
                          {item.canRenew && (
                            <ButtonIcon
                              icon="clock"
                              secondary
                              as={Button}
                              onClick={() => {
                                setProfile(item);
                                history.push(`/${type}/cis/certificates/renew`);
                              }}
                              style={{
                                backgroundColor: '#4caf50',
                                color: '#fff',
                                border: '1px solid #4caf50',
                              }}
                            />
                          )}
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setOpenConfirmModal(true);
                              setSelectedItem(item);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.certificates.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeCertificate(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        !isIpmaCvmb &&
        ReactDOM.createPortal(
          <Row style={{ alignItems: 'center', justifyContent: 'end' }}>
            {manageCisPermission && (
              <Col size={{ xs: 1 / 3 }}>
                <Button secondary as={Link} to={`/${type}/cis/certificates/create`}>
                  {t('page.user.cis.certificates.new')}
                </Button>
              </Col>
            )}
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary onClick={handleExport} disabled={isExporting}>
                {isExporting ? t('global.exporting') : t('global.export')}
              </Button>
            </Col>
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary onClick={handleNavigateExportReport}>
                {t('page.user.cis.certificates.exportReport')}
              </Button>
            </Col>
          </Row>,
          document.getElementById(`${type}-cis-buttons-portal`),
        )}
      {portalReady &&
        isIpmaCvmb &&
        ReactDOM.createPortal(
          <Row>
            <Col size={{ xs: 1 / 2 }}>
              <Button secondary onClick={handleExport} disabled={isExporting}>
                {isExporting ? t('global.exporting') : t('global.export')}
              </Button>
            </Col>
            <Col size={{ xs: 1 / 2 }}>
              <Button secondary onClick={handleNavigateExportReport}>
                {t('page.user.cis.certificates.exportReport')}
              </Button>
            </Col>
          </Row>,

          document.getElementById('ipma-cis-cvmb-statistics-actions-portal'),
        )}
    </PageContent>
  );
};

Certificates.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['ipma', 'ma', 'ipma-cvmb']),
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  certificationBodies: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomainsLevels: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomainsDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeCertificate: PropTypes.func.isRequired,
  organizations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
};

Certificates.defaultProps = {
  loading: false,
  type: 'ipma',
};

export default Certificates;
